/**
 * Push Notification Utility for Laprecio PWA
 * 
 * This utility provides functions to handle push notifications
 * for the Laprecio Progressive Web App.
 */

// Check if Push API is supported
const isPushSupported = 'serviceWorker' in navigator && 'PushManager' in window;

/**
 * Convert a base64 string to a Uint8Array
 * @param {string} base64String - The base64 string to convert
 * @returns {Uint8Array} The converted Uint8Array
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Request permission for push notifications
 * @returns {Promise<boolean>} Whether permission was granted
 */
export async function requestNotificationPermission() {
  if (!isPushSupported) {
    console.warn('Push notifications are not supported in this browser');
    return false;
  }

  try {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
}

/**
 * Subscribe to push notifications
 * @param {string} publicVapidKey - The public VAPID key for push notifications
 * @returns {Promise<PushSubscription|null>} The push subscription or null if failed
 */
export async function subscribeToPushNotifications(publicVapidKey) {
  if (!isPushSupported) {
    console.warn('Push notifications are not supported in this browser');
    return null;
  }

  try {
    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      console.warn('Notification permission was not granted');
      return null;
    }

    const registration = await navigator.serviceWorker.ready;
    
    // Get the existing subscription if any
    let subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      return subscription;
    }
    
    // Subscribe the user
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicVapidKey)
    });
    
    // Send the subscription to the server
    await sendSubscriptionToServer(subscription);
    
    return subscription;
  } catch (error) {
    console.error('Error subscribing to push notifications:', error);
    return null;
  }
}

/**
 * Unsubscribe from push notifications
 * @returns {Promise<boolean>} Whether unsubscription was successful
 */
export async function unsubscribeFromPushNotifications() {
  if (!isPushSupported) {
    console.warn('Push notifications are not supported in this browser');
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (!subscription) {
      console.warn('No push subscription found');
      return true;
    }
    
    // Unsubscribe from push notifications
    const success = await subscription.unsubscribe();
    
    if (success) {
      // Remove the subscription from the server
      await removeSubscriptionFromServer(subscription);
    }
    
    return success;
  } catch (error) {
    console.error('Error unsubscribing from push notifications:', error);
    return false;
  }
}

/**
 * Check if the user is subscribed to push notifications
 * @returns {Promise<boolean>} Whether the user is subscribed
 */
export async function isSubscribedToPushNotifications() {
  if (!isPushSupported) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch (error) {
    console.error('Error checking push subscription:', error);
    return false;
  }
}

/**
 * Send the push subscription to the server
 * @param {PushSubscription} subscription - The push subscription
 * @returns {Promise<Response>} The server response
 */
async function sendSubscriptionToServer(subscription) {
  // Replace with your actual API endpoint
  const endpoint = '/api/push-subscriptions';
  
  return fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      subscription,
      userAgent: navigator.userAgent
    })
  });
}

/**
 * Remove the push subscription from the server
 * @param {PushSubscription} subscription - The push subscription
 * @returns {Promise<Response>} The server response
 */
async function removeSubscriptionFromServer(subscription) {
  // Replace with your actual API endpoint
  const endpoint = '/api/push-subscriptions';
  
  return fetch(endpoint, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      subscription
    })
  });
}

/**
 * Show a local notification (not a push notification)
 * @param {string} title - The notification title
 * @param {Object} options - The notification options
 * @returns {Promise<Notification|null>} The notification or null if failed
 */
export async function showLocalNotification(title, options = {}) {
  if (!('Notification' in window)) {
    console.warn('Notifications are not supported in this browser');
    return null;
  }

  try {
    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      console.warn('Notification permission was not granted');
      return null;
    }
    
    // Set default options
    const defaultOptions = {
      icon: '/images/icons/icon-192x192.png',
      badge: '/images/icons/badge-72x72.png'
    };
    
    // Merge with provided options
    const notificationOptions = {
      ...defaultOptions,
      ...options
    };
    
    // Show the notification
    return new Notification(title, notificationOptions);
  } catch (error) {
    console.error('Error showing notification:', error);
    return null;
  }
}

export default {
  requestNotificationPermission,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  isSubscribedToPushNotifications,
  showLocalNotification
};
